import React, {FC, ReactElement, useCallback, useMemo, useRef, useState} from "react";
import {Divider, Step, StepContent, StepLabel, Stepper, Typography} from "@mui/material";
import LoaderWithCompanySelected from "../../../components/LoaderWithCompanySelected";
import {observer} from "mobx-react";
import PersonalDetailsSection from "./PersonalDetailsSection";
import ContractDetailsSection from "./ContractDetailsSection";
import TaxDetailsAndTemplatesSection from "./TaxDetailsAndTemplatesSection";
import DocumentsUploadSection from "./DocumentsUploadSection";
import ReviewSection from "./ReviewSection";
import {FileToUpload, FileUploadProgressHandle} from "../../../components/file/FileUploadProgress";
import UploadSection from "./UploadSection";
import {employeeApi} from "../../../backend/EmployeeApi";
import {uiStateStore} from "../../../store/UiState";
import {newEmployeeWizardState} from "../../../store/NewEmployeeWizardState";
import {Nil} from "../../../utils/Types";
import {logEvent} from "../../../utils/SentryUtils";
import {Trans, useTranslation} from "react-i18next";

interface IProps {
}

export const ControlDivider = () => {
    return <Divider style={{marginBottom: "0.25rem", marginTop: "0.25rem"}}/>
}

export const NewEmployeeWizard: FC<IProps> = observer((): ReactElement => {
    const [activeStep, setActiveStep] = useState(0);

    const handleNext = useCallback(() => {
        setActiveStep((current) => current + 1)
    }, []);

    const handleBack = useCallback(() => {
        setActiveStep((current) => current - 1)
    }, []);

    const docUploadRef = useRef<FileUploadProgressHandle>(null);
    const [uploadFinished, setUploadFinished] = useState(false);

    const onUploadDone = useCallback(() => {
        setUploadFinished(false);
        setActiveStep(0)
    }, []);

    const onStartUpload = useCallback(async () => {
        handleNext();
        setUploadFinished(false);
        const payload = {
            ...newEmployeeWizardState,
            document: newEmployeeWizardState.document?.name,
            workPermitDocument: newEmployeeWizardState.workPermitDocument?.name,
            taxResidenceStatementDocument: newEmployeeWizardState.taxResidenceStatementDocument?.name,
            taxDeductionFormDocument: newEmployeeWizardState.taxDeductionFormDocument?.name,
            workContractDocument: newEmployeeWizardState.workContractDocument?.name,
        }
        const withAlias = (file: File | Nil, prefix: string): FileToUpload | undefined => {
            if (file) {
                return {
                    alias: prefix + "__" + file.name,
                    file: file,
                }
            } else {
                return undefined;
            }
        }
        const files = [
            withAlias(newEmployeeWizardState.document, "Identity"),
            withAlias(newEmployeeWizardState.workPermitDocument, "WorkPermit"),
            withAlias(newEmployeeWizardState.taxResidenceStatementDocument, "TaxResidence"),
            withAlias(newEmployeeWizardState.taxDeductionFormDocument, "TaxDeduction"),
            withAlias(newEmployeeWizardState.workContractDocument, "Contract")
        ].filter((s): s is FileToUpload => Boolean(s))

        const sessionId = await employeeApi.startUpload(uiStateStore.language, payload, files.map(it => it.alias!!));
        logEvent("new_empl_doc_upload", `Session ${sessionId} was started`);

        const uploadDone = await docUploadRef.current!!.doUpload(sessionId, files)
        if (uploadDone) {
            await employeeApi.finishUpload(sessionId)
            logEvent("new_empl_doc_upload", `Session ${sessionId} is marked as finished`);
        }
        setUploadFinished(true)
        newEmployeeWizardState.resetState()
    }, [handleNext]);

    const {t} = useTranslation()

    const steps = useMemo(() => {
        return [
            {
                label: t("new_employee.personal_info.header", "Employee personal info"),
                content: () => <PersonalDetailsSection handleNext={handleNext}/>
            },
            {
                label: t("new_employee.contract_details.header", "Contract details"),
                content: () => <ContractDetailsSection handleNext={handleNext} handleBack={handleBack}/>
            },
            {
                label: t("new_employee.tax_and_templates.header", "Taxation"),
                content: () => <TaxDetailsAndTemplatesSection handleNext={handleNext} handleBack={handleBack}/>
            },
            {
                label: t("new_employee.document_upload.header", "Documents upload"),
                content: () => <DocumentsUploadSection handleNext={handleNext} handleBack={handleBack}/>
            },
            {
                label: t("new_employee.review.header", "Review"),
                content: () => <ReviewSection handleNext={onStartUpload} handleBack={handleBack}/>
            },
            {
                label: t("new_employee.upload.header", "Upload"),
                content: () => <UploadSection uploadSectionRef={docUploadRef} uploadFinished={uploadFinished} onClose={onUploadDone}/>
            },
        ]
    }, [handleNext, handleBack, docUploadRef, uploadFinished, onUploadDone, onStartUpload, t])

    return <>
        <LoaderWithCompanySelected loading={false}>
            <Typography variant="h5">
                <Trans i18nKey="new_employee.header">New employee notification</Trans>
            </Typography>
            <ControlDivider/>
            <Stepper orientation="vertical" activeStep={activeStep}>
                {steps.map((step, index) =>
                        <Step key={index}>
                            <StepLabel onClick={() => {
                                if (activeStep > index) {
                                    setActiveStep(index)
                                }
                            }}>
                                <Typography variant="h6">{step.label}</Typography>
                            </StepLabel>
                            <StepContent>
                                {index == activeStep && step.content()}
                            </StepContent>
                        </Step>
                )}
            </Stepper>
        </LoaderWithCompanySelected>
    </>
});

export default NewEmployeeWizard;